import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Partner } from '@tagadapay/crm-entities';

import { createTRPCClient } from '@/lib/api/trpc';
import { isTagadaHost } from '@/lib/branding/partner-link-preview';
import { config } from '@/lib/config';

/**
 * Runtime DB-driven branding overlay.
 *
 * Fetches the curated `partners.branding` subset — by CRM host
 * (`partner-domain`, public `/api/public/crm/partner-branding?host=`), or for
 * the signed-in active account (`reseller-only` orgs on app.tagada.io,
 * authenticated tRPC `v3.partnerBranding.forActiveAccount`; the public
 * endpoint no longer answers by slug). Self-contained fetch + module cache so
 * it works outside the react-query provider (App root, auth pages, etc.).
 */
type DbBranding = Partner.PartnerBranding;

const shouldSkipHost = (host: string): boolean =>
  !host || host === 'localhost' || host === '127.0.0.1' || isTagadaHost(host);

const BRANDING_CACHE_TTL_MS = 60_000;

type CacheEntry = { value: DbBranding | null; expiresAt: number };

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<DbBranding | null>>();

const hostCacheKey = (host: string): string => `host:${host}`;
const accountCacheKey = (orgId: string, slug: string): string => `account:${orgId}:${slug}`;

const isStale = (entry: CacheEntry): boolean => Date.now() > entry.expiresAt;

type Loader = () => Promise<DbBranding | null>;

const fetchBranding = async (query: string): Promise<DbBranding | null> => {
  try {
    const url = `${config.apiBaseUrl}/api/public/crm/partner-branding?${query}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = (await res.json()) as { branding?: unknown };
    return Partner.parsePartnerBranding(json?.branding);
  } catch {
    return null;
  }
};

export type BrandingLookupStatus = 'idle' | 'loading' | 'resolved';

type BrandingLookupResult = {
  branding: DbBranding | null;
  status: BrandingLookupStatus;
};

/**
 * `forActiveAccount` answers the V3 DTO: nulls for unset fields, which the
 * tolerant parser would reject, so they are dropped first.
 */
const fromAccountDto = (dto: unknown): DbBranding | null => {
  if (!dto || typeof dto !== 'object') return null;
  const set = Object.fromEntries(Object.entries(dto).filter(([, v]) => v !== null && v !== undefined));
  return Partner.parsePartnerBranding(set);
};

type GetToken = ReturnType<typeof useAuth>['getToken'];

const fetchActiveAccountBranding = async (getToken: GetToken): Promise<DbBranding | null> => {
  try {
    const client = createTRPCClient(getToken);
    const dto = await client.v3.partnerBranding.forActiveAccount.query();
    return fromAccountDto(dto);
  } catch {
    // Not signed in, no CRM V3 access (the V3 gate), network error: no overlay.
    return null;
  }
};

const useBrandingLookup = (key: string | null, load: Loader | null): BrandingLookupResult => {
  // `generation` bumps re-run the fetch effect after a focus revalidation marks
  // the cached entry stale (so a branding edit made in another app/tab is picked
  // up on return without a hard refresh).
  const [generation, forceRerender] = useState(0);

  useEffect(() => {
    if (!key || !load) return;
    const cached = cache.get(key);
    if (cached && !isStale(cached)) return; // fresh: nothing to fetch

    let active = true;
    const pending =
      inflight.get(key) ??
      load().then((value) => {
        cache.set(key, { value, expiresAt: Date.now() + BRANDING_CACHE_TTL_MS });
        inflight.delete(key);
        return value;
      });
    if (!inflight.has(key)) inflight.set(key, pending);

    const bump = () => {
      if (active) forceRerender((n) => n + 1);
    };
    pending.then(bump).catch(bump);

    return () => {
      active = false;
    };
    // `load` is keyed by `key`: a new closure for the same key must not refetch.
  }, [key, generation]);

  // Revalidate on tab focus/visibility. Stale-while-revalidate: mark the entry
  // stale (keeping its value on screen) and let the fetch effect refetch in the
  // background — never delete it, or the branding would blank on every refocus.
  useEffect(() => {
    if (!key) return;

    const revalidate = () => {
      const cached = cache.get(key);
      if (cached) cache.set(key, { ...cached, expiresAt: 0 });
      forceRerender((n) => n + 1);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') revalidate();
    };

    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [key]);

  // Serve the cached value even when stale (revalidation happens in the
  // background) so resolved branding never reverts to blank mid-session.
  const cached = key ? cache.get(key) : undefined;
  const branding = cached ? cached.value : null;
  const status: BrandingLookupStatus = !key ? 'idle' : cached ? 'resolved' : 'loading';

  return { branding, status };
};

export const fetchPartnerBrandingForHost = (host: string): Promise<DbBranding | null> =>
  fetchBranding(`host=${encodeURIComponent(host)}`);

const hostLoader = (host: string): Loader => () => fetchBranding(`host=${encodeURIComponent(host)}`);

const hostLookupArgs = (): [string | null, Loader | null] => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const key = shouldSkipHost(host) ? null : hostCacheKey(host);
  return [key, key ? hostLoader(host) : null];
};

const hostForHostLookupArgs = (host: string | null): [string | null, Loader | null] => {
  const normalized = host?.trim().toLowerCase() ?? '';
  const key = host && !shouldSkipHost(normalized) ? hostCacheKey(normalized) : null;
  return [key, key ? hostLoader(normalized) : null];
};

/**
 * The reseller lookup: keyed by active org + its whitelabel slug, loaded for
 * the active account (the server resolves the account's partner itself).
 */
const useSlugLookupArgs = (slug: string | null): [string | null, Loader | null] => {
  const { getToken, orgId, isSignedIn } = useAuth();
  const normalized = slug?.trim().toLowerCase() ?? '';
  const key = normalized && isSignedIn && orgId ? accountCacheKey(orgId, normalized) : null;
  const load = useMemo<Loader | null>(
    () => (key ? () => fetchActiveAccountBranding(getToken) : null),
    [key, getToken],
  );
  return [key, load];
};

/** DB branding for the current CRM host (`partner-domain` deploys). */
export function usePartnerBranding(): DbBranding | null {
  const [key, load] = hostLookupArgs();
  return useBrandingLookup(key, load).branding;
}

/** DB branding for an arbitrary host (e.g. partner `redirect_url` on primary sign-in). */
export function usePartnerBrandingForHost(host: string | null): DbBranding | null {
  const [key, load] = hostForHostLookupArgs(host);
  return useBrandingLookup(key, load).branding;
}

/**
 * DB branding for a reseller org (`reseller-only` on app.tagada.io): `slug` is
 * the active org's whitelabel tag; the branding is read for the active account.
 */
export function usePartnerBrandingForSlug(slug: string | null): DbBranding | null {
  const [key, load] = useSlugLookupArgs(slug);
  return useBrandingLookup(key, load).branding;
}

/** Fetch status for the current CRM host's DB branding — 'idle' when no lookup applies. */
export function usePartnerBrandingStatus(): BrandingLookupStatus {
  const [key, load] = hostLookupArgs();
  return useBrandingLookup(key, load).status;
}

/** Fetch status for an arbitrary host's DB branding — 'idle' when no lookup applies. */
export function usePartnerBrandingStatusForHost(host: string | null): BrandingLookupStatus {
  const [key, load] = hostForHostLookupArgs(host);
  return useBrandingLookup(key, load).status;
}

/** Fetch status for a reseller org slug's DB branding — 'idle' when slug is empty. */
export function usePartnerBrandingStatusForSlug(slug: string | null): BrandingLookupStatus {
  const [key, load] = useSlugLookupArgs(slug);
  return useBrandingLookup(key, load).status;
}
